import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_RELOAD_KEY = "rv_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 15_000;

export function isChunkLoadError(error: unknown): boolean {
  const msg = String(
    error instanceof Error ? error.message : error ?? "",
  );
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\d]+ failed|ChunkLoadError/i.test(
    msg,
  );
}

/** Reload once after a failed Vite chunk fetch (stale hash after deploy, or a brief 503 while Azure is starting). */
export function reloadOnceForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    const now = Date.now();
    if (last && now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importer();
    } catch (first) {
      if (!isChunkLoadError(first)) throw first;
      await new Promise((resolve) => setTimeout(resolve, 1200));
      try {
        return await importer();
      } catch (second) {
        if (isChunkLoadError(second) && reloadOnceForChunkError()) {
          return new Promise(() => {});
        }
        throw second;
      }
    }
  });
}
